/* eslint-disable max-lines-per-function */
import { ReactNode, useCallback } from 'react';

import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';
import { CurrentHealthcareProviderPaymentsConfigurationEnabledQuery } from '@dreryk/egabinet-online-payments-integration-frontend-api';

import { isDemo } from '@dreryk/demo-variant-frontend';
import {
   ModalCancelButton,
   ModalCloseButton,
   ModalConfirmButton,
   ModalWidth,
   ShowModalEventInterceptor,
   ValidateFormEventInterceptor,
} from '@dreryk/react-components';
import { performCqrsQueryFind } from '@dreryk/command-query-react-common';

import { protectFeature } from '~libs.shared.system';
import { assertUnreachable } from '~libs.typescript';

import { translationKeys } from '../translations';
import { advancePaymentFlowVisitor } from '../shared/advancePaymentFlowVisitor';
import { advancePaymentStatusVisitor } from '../shared/advancePaymentStatusVisitor';
import { phoneNumberSmsCapabilitiesVisitor } from '../shared/phoneNumberSmsCapabilitiesVisitor';

import {
   hasAnyReceptionServiceWithAdvancePayment,
   isAdvancePaymentOverpaid,
   performAppointmentAdvancePaymentCqrsFind,
   performAppointmentDetailsCqrsGet,
} from './AppointmentAdvancePaymentShowModalEventInterceptor.utils';
import { AdvancePaymentSettlementForm } from './settlement/underpayment/AdvancePaymentSettlementForm';
import { AdvancePaymentSendOnlinePaymentLinkToPatientActionEventInterceptor } from './settlement/underpayment/AdvancePaymentSendOnlinePaymentLinkToPatientActionEventInterceptor';
import { AdvancePaymentRefundActionEventInterceptor } from './settlement/overpayment/AdvancePaymentRefundActionEventInterceptor';
import { AdvancePaymentOverpaymentView } from './settlement/overpayment/AdvancePaymentOverpaymentView';
import { AdvancePaymentPaidView } from './summary/AdvancePaymentPaidView';

type AppointmentAdvancePaymentShowModalEventInterceptorProps = Readonly<{
   appointmentId: AppointmentId | null;
   children: ReactNode;
}>;

export const AppointmentAdvancePaymentShowModalEventInterceptor = (
   props: AppointmentAdvancePaymentShowModalEventInterceptorProps
): JSX.Element => {
   const shouldShowModal = useCallback(async () => {
      if (isDemo()) {
         return false;
      }
      const advancePaymentEnabled = protectFeature('searchingPrivateMedicalServices', true, false);
      const onlinePaymentsIntegrationExists = await performCqrsQueryFind(
         () => new CurrentHealthcareProviderPaymentsConfigurationEnabledQuery()
      );
      if (!advancePaymentEnabled || !onlinePaymentsIntegrationExists || !props.appointmentId) {
         return false;
      }

      const [appointmentDetails, advancePayment] = await Promise.all([
         performAppointmentDetailsCqrsGet(props.appointmentId),
         performAppointmentAdvancePaymentCqrsFind(props.appointmentId),
      ]);

      if (!advancePayment) {
         return false;
      }

      if (!isAdvancePaymentOverpaid(advancePayment) && !hasAnyReceptionServiceWithAdvancePayment(appointmentDetails)) {
         return false;
      }

      return {
         appointmentId: props.appointmentId,
         appointmentDetails: appointmentDetails,
         advancePayment: advancePayment,
      };
   }, [props.appointmentId]);

   return (
      <ShowModalEventInterceptor
         shouldShowModal={shouldShowModal}
         testId={'appointmentAdvancePayment'}
         title={({ result: { advancePayment } }) =>
            advancePaymentFlowVisitor(advancePayment, {
               visitSettlement: () => translationKeys.advancePaymentSettlement,
               visitSummary: () => translationKeys.advancePaymentSummary,
            })
         }
         footerItemsHidden
         modalWidth={ModalWidth.NORMAL}
         content={({ resolve, result: { appointmentId, appointmentDetails, advancePayment } }) => {
            return advancePaymentFlowVisitor(advancePayment, {
               visitSettlement: () =>
                  advancePaymentStatusVisitor(advancePayment, {
                     visitUnderpayment: () => (
                        <AdvancePaymentSettlementForm
                           appointmentDetails={appointmentDetails}
                           advancePayment={advancePayment}
                           patientPhoneNumber={appointmentDetails.patient.phoneNumber}
                        >
                           {({ handleSubmit, formState, trigger, watch }) => {
                              const patientPhoneNumber = watch('patientPhoneNumber');

                              return (
                                 <>
                                    <ValidateFormEventInterceptor trigger={trigger}>
                                       <AdvancePaymentSendOnlinePaymentLinkToPatientActionEventInterceptor
                                          appointmentId={appointmentId}
                                          handleSubmit={handleSubmit}
                                       >
                                          {phoneNumberSmsCapabilitiesVisitor(patientPhoneNumber, {
                                             visitNotProvided: () => (
                                                <ModalConfirmButton
                                                   testId={'generateLinkToPayment'}
                                                   label={translationKeys.generateAndSendLinkToPayment}
                                                   onClick={resolve}
                                                />
                                             ),
                                             visitSmsCapable: () => (
                                                <ModalConfirmButton
                                                   testId={'generateLinkToPayment'}
                                                   label={translationKeys.generateAndSendLinkToPayment}
                                                   onClick={resolve}
                                                />
                                             ),
                                             visitSmsIncapable: () => (
                                                <ModalConfirmButton
                                                   testId={'generateLinkToPayment'}
                                                   label={translationKeys.generateLinkToPayment}
                                                   onClick={resolve}
                                                />
                                             ),
                                          })}
                                       </AdvancePaymentSendOnlinePaymentLinkToPatientActionEventInterceptor>
                                    </ValidateFormEventInterceptor>
                                    <ModalCancelButton
                                       label={translationKeys.close}
                                       onClick={resolve}
                                       shouldConfirmExit={formState.isDirty}
                                    />
                                    <ModalCloseButton onClick={resolve} shouldConfirmExit={formState.isDirty} />
                                 </>
                              );
                           }}
                        </AdvancePaymentSettlementForm>
                     ),
                     visitPaid: assertUnreachable,
                     visitOverpayment: () => (
                        <>
                           <AdvancePaymentOverpaymentView
                              appointmentDetails={appointmentDetails}
                              advancePayment={advancePayment}
                           />
                           <AdvancePaymentRefundActionEventInterceptor advancePayment={advancePayment}>
                              <ModalConfirmButton
                                 testId={'returnOverpayment'}
                                 label={translationKeys.returnOverpayment}
                                 onClick={resolve}
                              />
                           </AdvancePaymentRefundActionEventInterceptor>
                           <ModalCancelButton label={translationKeys.close} onClick={resolve} />
                           <ModalCloseButton onClick={resolve} />
                        </>
                     ),
                  }),
               visitSummary: () => (
                  <>
                     <AdvancePaymentPaidView appointmentDetails={appointmentDetails} advancePayment={advancePayment} />
                     <ModalConfirmButton label={translationKeys.close} onClick={resolve} />
                     <ModalCloseButton onClick={resolve} />
                  </>
               ),
            });
         }}
      >
         {props.children}
      </ShowModalEventInterceptor>
   );
};
